import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Save, Globe } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImagePicker from "@/components/ImagePicker";

export default function AdminSettings() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newEmail, setNewEmail] = useState(user?.email || "");
    const [newName, setNewName] = useState(user?.name || "");
    const [newPassword, setNewPassword] = useState("");
    const [newPassword2, setNewPassword2] = useState("");
    const [busy, setBusy] = useState(false);

    if (!user) {
        nav("/login");
        return null;
    }

    const submit = async (e) => {
        e.preventDefault();
        if (!currentPassword) {
            toast.error("Inserisci la password attuale");
            return;
        }
        if (newPassword && newPassword !== newPassword2) {
            toast.error("Le due nuove password non coincidono");
            return;
        }
        const body = { current_password: currentPassword };
        if (newEmail && newEmail !== user.email) body.new_email = newEmail;
        if (newName && newName !== user.name) body.new_name = newName;
        if (newPassword) body.new_password = newPassword;
        if (!body.new_email && !body.new_name && !body.new_password) {
            toast.error("Non hai modificato nulla");
            return;
        }
        setBusy(true);
        try {
            await api.post("/auth/change-credentials", body);
            toast.success("Credenziali aggiornate");
            setCurrentPassword(""); setNewPassword(""); setNewPassword2("");
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="admin-settings-page" className="max-w-xl mx-auto px-5 py-14">
            <p className="text-xs tracking-[0.3em] uppercase text-abnormal-red font-bold mb-3 flex items-center gap-2">
                <Shield size={14} /> IMPOSTAZIONI ACCOUNT
            </p>
            <h1 className="font-display text-5xl uppercase leading-none">Cambia credenziali</h1>
            <p className="text-zinc-400 mt-4 text-sm">
                Cambia email, nome o password. Serve la password attuale per confermare. Dopo il salvataggio resti loggato automaticamente con i nuovi dati.
            </p>
            {user.email && user.email.includes("abnormaworld.it") && !user.email.includes("abnormalworld.it") && (
                <div className="mt-4 border border-abnormal-red/50 bg-abnormal-red/5 p-3 text-xs text-zinc-200">
                    ⚠️ La tua email admin è ancora <code className="text-abnormal-red">{user.email}</code> (senza L).
                    Cambiala in <code className="text-white">admin@abnormalworld.it</code> qui sotto.
                </div>
            )}

            <form onSubmit={submit} className="mt-10 space-y-5" data-testid="change-creds-form">
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Password attuale</label>
                    <input
                        type="password"
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        data-testid="settings-current-password"
                        className="mt-1 w-full bg-zinc-900 border border-zinc-800 focus:border-abnormal-red outline-none px-4 py-3"
                    />
                </div>

                <div className="border-t border-white/10 pt-5 space-y-5">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Nuovi valori (lascia vuoto quello che non vuoi cambiare)</p>

                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Nuova email</label>
                        <input
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            data-testid="settings-new-email"
                            className="mt-1 w-full bg-zinc-900 border border-zinc-800 focus:border-abnormal-red outline-none px-4 py-3"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Nome visualizzato</label>
                        <input
                            type="text"
                            maxLength={60}
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            data-testid="settings-new-name"
                            className="mt-1 w-full bg-zinc-900 border border-zinc-800 focus:border-abnormal-red outline-none px-4 py-3"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Nuova password (min. 6)</label>
                        <input
                            type="password"
                            minLength={6}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            data-testid="settings-new-password"
                            className="mt-1 w-full bg-zinc-900 border border-zinc-800 focus:border-abnormal-red outline-none px-4 py-3"
                            placeholder="lascia vuoto per non cambiarla"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Ripeti nuova password</label>
                        <input
                            type="password"
                            value={newPassword2}
                            onChange={(e) => setNewPassword2(e.target.value)}
                            data-testid="settings-new-password2"
                            className="mt-1 w-full bg-zinc-900 border border-zinc-800 focus:border-abnormal-red outline-none px-4 py-3"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="settings-save-btn"
                    className="w-full bg-abnormal-red text-white uppercase tracking-wider font-bold py-3 hover:bg-white hover:text-abnormal-red transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                >
                    <Save size={16} /> {busy ? "Salvo…" : "Salva credenziali"}
                </button>
            </form>

            <EcosystemSettings />
        </div>
    );
}

function EcosystemSettings() {
    const [s, setS] = useState({ shop_url: "/shop", murph_url: "/abnormal#murph", murph_images: [], tes_route_url: "", tes_route_label: "", tes_ground_note: "", tes_ground_note_en: "", wet_mm_24h: 10, wet_mm_6h: 5, stale_hours: 12 });
    const [saving, setSaving] = useState(false);
    useEffect(() => { api.get("/settings").then((r) => setS((p) => ({ ...p, ...r.data }))).catch(() => {}); }, []);

    const setImg = (i, v) => { const arr = [...(s.murph_images || [])]; while (arr.length < 3) arr.push(""); arr[i] = v; setS({ ...s, murph_images: arr }); };
    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/settings", { ...s, murph_images: (s.murph_images || []).filter(Boolean) });
            toast.success("Link ecosistema salvati");
        } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
        finally { setSaving(false); }
    };
    const imgs = [0, 1, 2].map((i) => (s.murph_images || [])[i] || "");
    const cls = "mt-1 w-full bg-zinc-900 border border-zinc-800 focus:border-abnormal-acid outline-none px-4 py-3";

    return (
        <form onSubmit={save} data-testid="ecosystem-settings-form" className="mt-16 border-t border-white/10 pt-10 space-y-5">
            <p className="text-xs tracking-[0.3em] uppercase text-abnormal-acid font-bold flex items-center gap-2"><Globe size={14} /> ECOSISTEMA · ABNORMALWORLD.COM</p>
            <h2 className="font-display text-4xl uppercase leading-none">Link & Murph</h2>
            <p className="text-zinc-400 text-sm">Gli URL usati dai bottoni "Entra nello shop" e "Leggi la storia di Murph". Le 3 immagini riempiono le tavole a fumetti nella sezione Meet Murph.</p>
            <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">URL shop (interno: /shop)</label>
                <input type="text" value={s.shop_url} onChange={(e) => setS({ ...s, shop_url: e.target.value })} data-testid="settings-shop-url" className={cls} />
            </div>
            <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">URL storia di Murph</label>
                <input type="text" value={s.murph_url} onChange={(e) => setS({ ...s, murph_url: e.target.value })} data-testid="settings-murph-url" className={cls} placeholder="/abnormal#murph" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Percorso ufficiale TES · URL (Komoot/Strava/GPX)</label>
                    <input type="url" value={s.tes_route_url || ""} onChange={(e) => setS({ ...s, tes_route_url: e.target.value })} data-testid="settings-tes-route-url" className={cls} placeholder="https://…" />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Etichetta link percorso</label>
                    <input value={s.tes_route_label || ""} onChange={(e) => setS({ ...s, tes_route_label: e.target.value })} data-testid="settings-tes-route-label" className={cls} placeholder="Percorso ufficiale · Komoot" />
                </div>
            </div>
            <div className="border border-abnormal-acid/40 p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-abnormal-acid font-bold">Meteo ultimo minuto · Condizioni sul campo (TES 04.10)</p>
                    {(s.tes_ground_note || s.tes_ground_note_en) && (
                        <button type="button" onClick={() => setS({ ...s, tes_ground_note: "", tes_ground_note_en: "" })} data-testid="settings-ground-note-clear"
                            className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white font-bold">Svuota</button>
                    )}
                </div>
                <p className="text-xs text-zinc-500">Compare accanto alla previsione nel blocco evento. Vuoto = nascosto. La data/ora di aggiornamento è automatica.</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Nota IT</label>
                        <textarea rows={3} maxLength={280} value={s.tes_ground_note || ""} onChange={(e) => setS({ ...s, tes_ground_note: e.target.value })} data-testid="settings-ground-note-it" className={cls}
                            placeholder="Es. Fondo asciutto su PS1-PS3, Senal umida in ombra. Cielo coperto, niente pioggia al paddock." />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Nota EN (opzionale)</label>
                        <textarea rows={3} maxLength={280} value={s.tes_ground_note_en || ""} onChange={(e) => setS({ ...s, tes_ground_note_en: e.target.value })} data-testid="settings-ground-note-en" className={cls}
                            placeholder="E.g. Dry on PS1-PS3, Senal damp in the shade. Overcast, no rain at the paddock." />
                    </div>
                </div>
            </div>
            <div className="border border-white/10 p-4 space-y-3" data-testid="settings-wet-thresholds">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Trail bagnati — soglie pioggia (Open-Meteo, controllo ogni 3 h)</p>
                <p className="text-xs text-zinc-500">Default: 10 mm/24 h = "pioggia moderata" accumulata in un giorno · 5 mm/6 h = rovescio intenso. Sopra soglia i trail aperti passano a "Bagnato · auto"; mai impostati asciutti in automatico.</p>
                <div className="grid grid-cols-3 gap-3">
                    <label className="text-xs text-zinc-400">mm / 24 h
                        <input type="number" step="0.5" min="1" max="100" value={s.wet_mm_24h ?? 10} onChange={(e) => setS({ ...s, wet_mm_24h: Number(e.target.value) })} data-testid="settings-wet-24h" className="mt-1 w-full bg-black border border-white/15 px-2 py-1.5 text-white" />
                    </label>
                    <label className="text-xs text-zinc-400">mm / 6 h
                        <input type="number" step="0.5" min="0.5" max="50" value={s.wet_mm_6h ?? 5} onChange={(e) => setS({ ...s, wet_mm_6h: Number(e.target.value) })} data-testid="settings-wet-6h" className="mt-1 w-full bg-black border border-white/15 px-2 py-1.5 text-white" />
                    </label>
                    <label className="text-xs text-zinc-400">Dati vecchi dopo (h)
                        <input type="number" min="3" max="72" value={s.stale_hours ?? 12} onChange={(e) => setS({ ...s, stale_hours: Number(e.target.value) })} data-testid="settings-stale-hours" className="mt-1 w-full bg-black border border-white/15 px-2 py-1.5 text-white" />
                    </label>
                </div>
            </div>
            {imgs.map((v, i) => (
                <div key={i}>
                    <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Tavola Murph {i + 1}</label>
                    <ImagePicker value={v} onChange={(u) => setImg(i, u)} testidPrefix={`settings-murph-img-${i}`} />
                </div>
            ))}
            <button type="submit" disabled={saving} data-testid="settings-eco-save-btn"
                className="w-full bg-abnormal-acid text-black uppercase tracking-wider font-bold py-3 hover:bg-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                <Save size={16} /> {saving ? "Salvo…" : "Salva ecosistema"}
            </button>
        </form>
    );
}
